import { Activity, lazy } from 'react'

import { useUser } from '@clerk/react'
import {
	Book,
	BookOpenText,
	Building2,
	CircleQuestionMark,
	Cloud,
	File,
	Home,
	Logs,
	Network,
	Play,
	Search,
	Server,
} from 'lucide-react'

import { appVersion, isDemoPresentation, isPRODDeploy } from '@/lib/constants'

import { DemoHidden } from '@/features/demo/components/demo-hidden'
import { DemoTourSidebarButton } from '@/features/demo/tour/components/demo-tour-sidebar-button'
import { Graylog } from '@/features/graylog/components/icon'
import { useCheckIntegrations } from '@/features/integrations/hooks/use-check-integrations'

import { HasActiveAnalyst } from '@/shared/auth/components/has-active-analyst'
import { HasRole } from '@/shared/auth/components/has-role'
import { ROLES } from '@/shared/auth/constants'
import { useMe } from '@/shared/auth/queries'
import { useIsOwnerTenant, useTenantDetails } from '@/shared/auth/store'
import { WhatsNewSidebarButton } from '@/shared/beta-announcement/components/whats-new-sidebar-button'

import { LogoWhite } from '@/components/icons/bitlyft-logo'
import { Separator } from '@/components/ui/separator'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
} from '@/components/ui/sidebar'
import { Skeleton } from '@/components/ui/skeleton'

import UserButton from './app-user-button'
import { SidebarCustomDashboards } from './sidebar-custom-dashboards'
import { SidebarLink } from './sidebar-link'
import { SidebarTenantSelectSkeleton } from './skeletons'

const SidebarTenantSelect = lazy(() => import('./app-tenant-select'))

/**
 * Sidebar component
 * - Contains the logo and menu, memoized to prevent unnecessary re-renders
 */
export const AppSidebar = () => {
	const { user } = useUser()

	const isOwnerTenant = useIsOwnerTenant()

	const { hasGraylog: hasGraylogIntegration } = useCheckIntegrations()
	const {
		data: { sku_info },
	} = useTenantDetails()

	const hasSearch = sku_info.is_search_enabled
	const hasMDR = sku_info.is_mdr_enabled

	const {
		data: { allowed_tenants },
	} = useMe()

	// Demo containment (ADR 0006 dec. 5 · issue 03): strip the nav items for
	// demo-excluded surfaces. Belt-and-suspenders with the `beforeLoad` redirect
	// guard — the guard blocks deep-links, this keeps the excluded links out of
	// sight. Source of truth for the route set is `features/demo/excluded-routes`.
	const hasGraylog = hasGraylogIntegration && !isDemoPresentation
	const hasMultipleTenants = allowed_tenants.length > 1 && !isDemoPresentation

	const hasApplication = hasSearch || hasGraylog

	// Onboarding is Graylog-integration setup (a write flow), so it is off in
	// the demo along with its Wizard and the /home task list (ticket 03).
	const canShowOnboarding =
		!user?.unsafeMetadata.onboarding_progress?.quickStart.completed &&
		isOwnerTenant &&
		!isDemoPresentation

	return (
		<Sidebar variant="inset" collapsible="icon" className="z-20">
			<SidebarHeader>
				<SidebarTenantSelect />
				<Separator className="mt-3" />
			</SidebarHeader>
			<SidebarContent>
				{/* First by design (ADR 0015): in the demo the tour is the product
				    pitch, so it sits above the app's own navigation. Renders null
				    outside the demo build. */}
				<DemoTourSidebarButton />

				<SidebarOnboarding canShowOnboarding={canShowOnboarding} />

				<SidebarMultiTenant hasMultipleTenants={hasMultipleTenants} />

				<SidebarApplications
					hasApplication={hasApplication}
					hasSearch={hasSearch}
					hasGraylog={hasGraylog}
				/>

				<SidebarCustomDashboards />

				<SidebarResponse />

				<SidebarIntegrations />

				<SidebarReports hasMDR={hasMDR} hasSearch={hasSearch} />

				<div className="flex-auto"></div>

				<DemoHidden>
					<WhatsNewSidebarButton />
				</DemoHidden>

				<SidebarOtherLinks />
			</SidebarContent>

			<SidebarFooter>
				<DemoHidden>
					<UserButton />
				</DemoHidden>
				<p className="mt-2 text-center text-xs">v{appVersion}</p>
			</SidebarFooter>
		</Sidebar>
	)
}

export const AppSidebarSkeleton = () => {
	return (
		<Sidebar variant="inset" collapsible="icon" className="z-20">
			<SidebarHeader>
				<SidebarTenantSelectSkeleton />
				<Separator className="mt-3" />
			</SidebarHeader>

			<SidebarContent>
				{/* All skeleton groups — no auth hooks allowed here (renders before beforeLoad) */}
				{Array.from({ length: 3 }).map((_, i) => (
					<SidebarGroup key={i}>
						<SidebarGroupLabel>
							<Skeleton className="h-4 w-24" />
						</SidebarGroupLabel>
						<SidebarMenu>
							{Array.from({ length: 2 }).map((_, j) => (
								<SidebarMenuItem key={j}>
									<SidebarMenuButton
										tooltip="Loading..."
										render={<Skeleton className="h-8 w-full" />}
									/>
								</SidebarMenuItem>
							))}
						</SidebarMenu>
					</SidebarGroup>
				))}

				<div className="flex-auto"></div>

				<SidebarOtherLinks />
			</SidebarContent>

			<SidebarFooter>
				<Skeleton className="size-8" />
				<p className="mt-2 text-center text-xs">v{appVersion}</p>
			</SidebarFooter>
		</Sidebar>
	)
}

function SidebarOnboarding({
	canShowOnboarding,
}: {
	canShowOnboarding: boolean
}) {
	return (
		<Activity mode={canShowOnboarding ? 'visible' : 'hidden'}>
			<SidebarGroup>
				<SidebarGroupLabel>Onboarding</SidebarGroupLabel>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarLink to="/home" tooltip="Onboarding">
							<Home />
							<span>Home</span>
						</SidebarLink>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarGroup>
		</Activity>
	)
}

function SidebarApplications({
	hasApplication,
	hasSearch,
	hasGraylog,
}: {
	hasApplication: boolean
	hasSearch: boolean
	hasGraylog: boolean
}) {
	return (
		<Activity mode={hasApplication ? 'visible' : 'hidden'}>
			<SidebarGroup>
				<SidebarGroupLabel>Application</SidebarGroupLabel>

				<SidebarMenu>
					<Activity mode={hasSearch ? 'visible' : 'hidden'}>
						<SidebarMenuItem>
							<SidebarLink to="/air" tooltip="Dashboard ">
								<LogoWhite />
								<span>Dashboard </span>
							</SidebarLink>
						</SidebarMenuItem>
					</Activity>

					<Activity mode={hasGraylog ? 'visible' : 'hidden'}>
						<SidebarMenuItem>
							<SidebarLink tooltip="Dashboard" to="/graylog">
								<Graylog />
								<span>Dashboard</span>
							</SidebarLink>
						</SidebarMenuItem>
					</Activity>

					<Activity mode={hasSearch ? 'visible' : 'hidden'}>
						<HasActiveAnalyst>
							<SidebarMenuItem>
								<SidebarLink tooltip="Log Search" to="/search">
									<Search />
									<span>Log Search</span>
								</SidebarLink>
							</SidebarMenuItem>
						</HasActiveAnalyst>
					</Activity>
				</SidebarMenu>
			</SidebarGroup>
		</Activity>
	)
}

function SidebarMultiTenant({
	hasMultipleTenants,
}: {
	hasMultipleTenants: boolean
}) {
	return (
		<Activity mode={hasMultipleTenants ? 'visible' : 'hidden'}>
			<SidebarGroup>
				<SidebarGroupLabel>Tenant Management</SidebarGroupLabel>
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarLink to="/multi-tenant" tooltip="Overview">
							<Building2 />
							<span>Overview</span>
						</SidebarLink>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarGroup>
		</Activity>
	)
}

function SidebarResponse() {
	return (
		<SidebarGroup>
			<SidebarGroupLabel>Automation Response</SidebarGroupLabel>
			<SidebarMenu>
				<HasRole role={ROLES.Analyst}>
					<SidebarMenuItem>
						<SidebarLink tooltip="Playbooks" to="/playbooks">
							<Book />
							<span>Playbooks</span>
						</SidebarLink>
					</SidebarMenuItem>

					<SidebarMenuItem>
						<SidebarLink tooltip="Actions" to="/actions">
							<Play />
							<span>Actions</span>
						</SidebarLink>
					</SidebarMenuItem>
				</HasRole>

				<SidebarMenuItem>
					<SidebarLink to="/activity" tooltip="Activity">
						<Logs />
						<span>Activity</span>
					</SidebarLink>
				</SidebarMenuItem>
			</SidebarMenu>
		</SidebarGroup>
	)
}

/**
 * Temporary prod reversal (owner-confirmed): the `/integrations` Overview
 * page isn't ready for prod yet, so prod keeps the old single "Management"
 * entry — pointed at `/integrations/cloud` (today's de-facto landing page),
 * NOT `/integrations` (that route is the new, not-yet-prod-ready Overview).
 * Every other stage (local, deployed dev, staging, and the demo build) gets
 * the current three-item group.
 *
 * `isPRODDeploy` is a build-time constant (never flips at runtime), so this
 * is a plain conditional rather than the file's `<Activity>` idiom — Activity
 * is for hiding a subtree whose state must survive a runtime toggle (e.g.
 * `hasApplication`, sourced from query data); there's nothing to preserve
 * across two branches that are fixed for the life of the bundle. This
 * matches how the file already treats `isDemoPresentation`, another
 * build-time constant, at :389/:409 — a plain ternary, not `<Activity>`.
 *
 * ENDS WHEN: the Overview page ships to prod — delete the `isPRODDeploy`
 * branch and this comment, leaving only the three-item group.
 */
export function SidebarIntegrations() {
	return (
		<SidebarGroup>
			<SidebarGroupLabel>Integrations</SidebarGroupLabel>
			<SidebarMenu>
				{isPRODDeploy ? (
					<SidebarMenuItem>
						<SidebarLink
							tooltip="Integrations Management"
							to="/integrations/cloud"
						>
							<Server />
							<span>Management</span>
						</SidebarLink>
					</SidebarMenuItem>
				) : (
					<>
						<SidebarMenuItem>
							<SidebarLink tooltip="All Integrations" to="/integrations" exact>
								<Server />
								<span>All</span>
							</SidebarLink>
						</SidebarMenuItem>

						<SidebarMenuItem>
							<SidebarLink
								tooltip="Cloud Integrations"
								to="/integrations/cloud"
							>
								<Cloud />
								<span>Cloud</span>
							</SidebarLink>
						</SidebarMenuItem>

						<SidebarMenuItem>
							<SidebarLink
								tooltip="Network Collectors"
								to="/integrations/collectors"
							>
								<Network />
								<span>Network Collectors</span>
							</SidebarLink>
						</SidebarMenuItem>
					</>
				)}
			</SidebarMenu>
		</SidebarGroup>
	)
}

function SidebarReports({
	hasMDR,
	hasSearch,
}: {
	hasMDR: boolean
	hasSearch: boolean
}) {
	return (
		<Activity mode={hasMDR || hasSearch ? 'visible' : 'hidden'}>
			<SidebarGroup>
				<SidebarGroupLabel>Reports</SidebarGroupLabel>
				<SidebarMenu>
					<Activity mode={hasMDR ? 'visible' : 'hidden'}>
						<SidebarMenuItem>
							<SidebarLink
								tooltip="Executive Reports"
								to="/mdr/executive-reports"
							>
								<File />
								<span>Executive Reports</span>
							</SidebarLink>
						</SidebarMenuItem>
					</Activity>

					<Activity mode={hasSearch ? 'visible' : 'hidden'}>
						<SidebarMenuItem>
							<SidebarLink tooltip="Reports" to="/report">
								<File />
								<span>Reports</span>
							</SidebarLink>
						</SidebarMenuItem>
					</Activity>
				</SidebarMenu>
			</SidebarGroup>
		</Activity>
	)
}

function SidebarOtherLinks() {
	return (
		<SidebarGroup>
			<SidebarGroupLabel>Other Links</SidebarGroupLabel>
			<SidebarMenu>
				<SidebarMenuItem>
					<SidebarMenuButton
						tooltip="Support Portal"
						render={
							<a
								href={
									isDemoPresentation
										? 'https://www.bitlyft.com/request-a-demo'
										: 'https://bitlyft.atlassian.net/servicedesk/customer/portal/5/group/19'
								}
								target="_blank"
								rel="noopener noreferrer"
							>
								<CircleQuestionMark />
								<span>Support Portal</span>
							</a>
						}
					/>
				</SidebarMenuItem>

				<SidebarMenuItem>
					<SidebarMenuButton
						tooltip="Help Docs"
						render={
							<a
								href={
									isDemoPresentation
										? 'https://www.bitlyft.com/request-a-demo'
										: 'https://bitlyft.atlassian.net/wiki/spaces/BAHD/overview?homepageId=824672628'
								}
								target="_blank"
								rel="noopener noreferrer"
							>
								<BookOpenText />
								<span>Help Docs</span>
							</a>
						}
					/>
				</SidebarMenuItem>
			</SidebarMenu>
		</SidebarGroup>
	)
}
